import { BeatSequencer } from '@/story/sequencer';
import { BORN, BORN_CUES, BORN_FLAGS, BORN_IDS } from '@/story/act1/born';
import {
  BAY_Z, BORN_DOOR_WATCH_AT, BORN_MOTHER_FLANK_AT, BORN_SIBLING_AT, BORN_STRAW_AT, DECK_Y, SIBLING_BOUNDS, awakeSiblingSolid,
} from '@/world/bornBay';
import { BornDirector, type BornDebug } from '@/world/bornDirector';
import { BornSibling } from '@/world/bornSibling';
import { takeLivePenGate } from '@/world/livePenGate';
import type { ColliderHandle } from '@/game/roomModule';
import type { Interactable } from '@/systems/interaction';
import type { BeatPartCapabilities, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';

/**
 * Act 1 beat 1, *Born*: the bay, her mother, the straw, the door, and the
 * littermate who is taken.
 *
 * Until session 85 this was a branch in `main.ts` plus lines in eight other
 * places there (D-234). Where each piece now runs, against where it ran:
 *
 * - The two nudges (her mother answers; the empty straw does not) ran in the
 *   interaction-event loop; `fixedStep` reads them as this tick's edges, still
 *   before `story.update` in the same tick.
 * - The sibling's collider removal is requested on the RENDER step (the
 *   director takes the body there) and was consumed by a host latch at the very
 *   start of the next fixed tick, traced as `bornSiblingTaken`. It still is —
 *   through `world.atNextTick`, which is that latch made generic.
 * - The shot cuts, with her interpolated position (`framing(her)`), as the
 *   legacy chain's first term did.
 */
function createBornPartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const world = capabilities.world;
  if (world === undefined) throw new Error('beat 1 / part 1: the host offered no world capability');
  const farrowing = world.room.farrowing;
  if (farrowing === undefined) throw new Error('beat 1 / part 1: the room offers no farrowing seam — booted outside the farrowing hall');
  const bay = farrowing.bay;
  const sowVoiceAt = { x: bay.sow.breath.parent?.position.x ?? 0, y: 0.45, z: BAY_Z };

  const director = new BornDirector(bay, {
    // The boots are on concrete, on the walkway, and she is on straw in a
    // pen. Two surfaces in one room, which is the first time in this project
    // that `GameAudio.surface` describing one floor has been a lie.
    onBootfall: (at) => world.playOneShot('footfall-concrete', { gain: 0.5, position: at, detune: 70 }),
    // Fired at the moment the body leaves the scene, which since D-038 is when
    // the pen is measured dark — NOT on the `taken` cue two seconds later.
    // Hanging this off the cue left a window in which the straw was visibly
    // empty and still solid: an invisible littermate, in the dark, exactly
    // where the beat wants her snout. The director runs on the render step, so
    // it may request this but must never mutate Rapier itself.
    // Both colliders: the sleeper's, which the room owns, and the awake one's,
    // which this part added when it woke (D-250). The empty patch is wherever
    // it actually lay, which since it can get up is not always its bed.
    onSiblingTaken: () => world.atNextTick('bornSiblingTaken', () => {
      farrowing.removeTakenSiblingCollider();
      if (siblingCollider !== null) world.colliders.remove(siblingCollider);
      siblingCollider = null;
      siblingGone = true;
      const lay = sibling.snapshot;
      world.interaction.moveTo(BORN_IDS.empty, { x: lay.x, y: DECK_Y + 0.05, z: lay.z });
      world.interaction.setEnabled(BORN_IDS.sibling, false);
    }),
    // Her voice. Until D-250 it was Rosalie's breath slowed and dropped,
    // because the registry had no pig in it; a grunt the player can name is
    // what makes the first verb the game teaches unmistakably answered.
    onSowAnswer: () => world.playOneShot('sow-grunt', { gain: 0.9, position: sowVoiceAt, detune: 60 }),
    // The same grunt, louder and higher, three times: she heard her litter
    // scream and cannot turn round to see why (D-250).
    onSowAlarm: () => world.playOneShot('sow-grunt', { gain: 1.3, position: sowVoiceAt, detune: 140 }),
    onSiblingSqueak: (at) => world.playOneShot('piglet-squeak', { gain: 0.8, position: at, detune: 80 }),
    onSiblingCry: (at, gain) => world.playOneShot('piglet-squeal', { gain, position: at, detune: 60 }),
    // Steel on steel, small: the trailer race's clang, quieter and a fifth up.
    onLatch: (at) => world.playOneShot('race-clang', { gain: 0.35, position: at, detune: 700 }),
  }, farrowing.walkwayDoorHinge, takeLivePenGate(world.scene));
  const sibling = new BornSibling(SIBLING_BOUNDS);
  /** The awake sibling's own collider, added when it wakes (the room's is the sleeper's). */
  let siblingCollider: ColliderHandle | null = null;
  /** Set on the tick the body leaves; the sibling stops being simulated there. */
  let siblingGone = false;
  const siblingPose = { position: { x: 0, y: 0, z: 0 }, quaternion: { x: 0, y: 0, z: 0, w: 1 } };
  const story = new BeatSequencer(BORN);

  // What she can put her snout on, and it is deliberately a short list. Spec
  // §7's "tutorial by curiosity" only works if there is very little to be
  // curious about.
  const interactables: readonly Interactable[] = [
    {
      id: BORN_IDS.mother,
      // Her flank, at the bars — not her head. The nearest part of her mother
      // is the part Rosalie can actually reach.
      at: BORN_MOTHER_FLANK_AT,
      verbs: ['nudge'],
      reach: 0.75,
    },
    {
      id: BORN_IDS.sibling,
      // Asleep under the lamp; `afterMove` moves this with it once it is up.
      at: BORN_SIBLING_AT,
      verbs: ['nudge'],
      reach: 0.7,
      // Offered on `offerSibling`, after her mother (D-250), and withdrawn
      // when it goes back to lie down: a prompt on a sleeping littermate asks
      // the player to wake it again, and the beat has moved on.
      enabled: false,
    },
    {
      id: BORN_IDS.straw,
      at: BORN_STRAW_AT,
      verbs: ['nudge', 'root'],
      reach: 0.6,
      duration: 2.4,
      // Offered on `offerStraw`, not from boot. The beat teaches nudge → root
      // → look in that order: the straw is the fun one, and a player who finds
      // it first has no reason to go back to the bars.
      enabled: false,
    },
    {
      id: BORN_IDS.door,
      // The doorway, watched from across the pen. `observe` gets a 3× reach
      // multiplier, which is what makes a thing 8 m away watchable at all.
      at: BORN_DOOR_WATCH_AT,
      verbs: ['observe'],
      mapGlyph: 'door',
      reach: 3.5,
      duration: 3.2,
      // Offered on `offerDoor`, and **this one was the bug Alex found**: "what
      // is that 'watch' interaction with the sow?" The 3× multiplier turns this
      // reach into 10.5 m and she wakes about 1 m from the door, so WATCH was
      // live from the first frame, from everywhere, and read as belonging to
      // her mother.
      enabled: false,
    },
    {
      id: BORN_IDS.empty,
      at: BornDirector.emptyAt,
      verbs: ['nudge', 'root'],
      reach: 0.55,
      duration: 2,
      // Disabled until there is an empty patch. Enabling it before the taking
      // would put her snout on a spot where her littermate is still lying.
      enabled: false,
    },
  ];
  let disposed = false;

  return {
    story,
    interactables,
    get fade(): number { return director.fade; },
    // The opening reveal holds the lens, then the fade (the legacy terms).
    get promptSuppressed(): boolean { return !disposed && (director.cameraHeld || director.fade > 0.5); },
    // Its shots always cut, and an eased version of the same shot is a different shot.
    framingCuts: true,
    framing: (her) => (disposed ? null : director.framing(her)),
    // Before interaction sampling, so this tick's nudge finds it where it is.
    afterMove({ dt, player }): void {
      if (siblingGone) return;
      sibling.step(dt, player);
      if (!sibling.awake && sibling.snapshot.phase === 'asleep') return;
      const s = sibling.snapshot;
      world.interaction.moveTo(BORN_IDS.sibling, { x: s.x, y: DECK_Y + 0.15, z: s.z });
      if (siblingCollider !== null) {
        const solid = awakeSiblingSolid(s);
        siblingPose.position.x = solid.pos.x;
        siblingPose.position.y = solid.pos.y;
        siblingPose.position.z = solid.pos.z;
        world.colliders.setPose(siblingCollider, siblingPose);
      }
    },
    fixedStep({ raiseFlag, nudged }): void {
      // One nudge is enough. Requiring several would turn a greeting into a
      // task, and spec §7 asks for tutorial by curiosity.
      if (nudged?.(BORN_IDS.mother) === true) {
        director.answer();
        raiseFlag(BORN_FLAGS.motherAnswered);
      }
      // Deliberately nothing to hear. She puts her snout in the flattened
      // straw and the game does not answer, because there is nothing there to
      // answer. That absence is the point and it must not be softened.
      if (nudged?.(BORN_IDS.empty) === true) raiseFlag(BORN_IDS.empty);
      // Her sibling wakes to the first nudge and answers every one after it
      // while it is up (D-250). On waking, the sleeper's collider — a box the
      // shape of a curled piglet on its bed — becomes the awake one's, which
      // follows it and is low enough that she cannot climb it.
      if (!siblingGone && nudged?.(BORN_IDS.sibling) === true && sibling.nudge()) {
        raiseFlag(BORN_FLAGS.siblingWoken);
        farrowing.removeTakenSiblingCollider();
        siblingCollider = world.colliders.add(awakeSiblingSolid(sibling.snapshot));
      }
    },
    handleCue(cue): void {
      director.handle(cue as Parameters<BornDirector['handle']>[0]);
      // The verbs the tutorial hands out, in the order it hands them out. Both
      // cues were emitted and nothing listened, once, so all of them were live
      // from boot and the authored order was enforced by nothing.
      if (cue.id === BORN_CUES.offerSibling) world.interaction.setEnabled(BORN_IDS.sibling, true);
      if (cue.id === BORN_CUES.offerStraw) world.interaction.setEnabled(BORN_IDS.straw, true);
      if (cue.id === BORN_CUES.offerDoor) world.interaction.setEnabled(BORN_IDS.door, true);
      // Something at the door, and her sibling goes back under the lamp. The
      // boots are the backstop for a look stage that timed out: whatever the
      // player did, it is lying down by the time anybody leans in.
      if (cue.id === BORN_CUES.offerDoor || cue.id === BORN_CUES.bootsEnter) {
        sibling.settle();
        world.interaction.setEnabled(BORN_IDS.sibling, false);
      }
      // The collider is NOT removed here: `onSiblingTaken` does it, at the
      // moment the body leaves the scene, which is earlier than this cue.
      if (cue.id === BORN_CUES.taken) world.interaction.setEnabled(BORN_IDS.empty, true);
    },
    renderStep(frameDt, her): void {
      if (disposed) return;
      director.presentSibling(sibling.snapshot);
      director.update(frameDt, her);
    },
    diagnostics(): { readonly kind: 'born'; readonly born: BornDebug } {
      return {
        kind: 'born',
        born: {
          // Read off the LIGHT rather than off the director's own target, so
          // this reports what is actually in the frame (D-026), against the
          // intensity the ZONE chose, captured before anything dimmed it.
          lampLevel: farrowing.lampFullIntensity > 0 ? (bay.lamp?.intensity ?? 0) / farrowing.lampFullIntensity : 0,
          // The director's measured progress, not a second interpolation.
          lean: director.lean,
          // Through the live fixtures: a dim that was requested but never
          // reached the room must show, not repeat the request.
          nearLightLevel: director.nearLightLevel,
          fade: director.fade,
          cameraHeld: director.cameraHeld,
          // Read off the scene graph: only its parent can say the littermate
          // is not in the frame.
          siblingPresent: bay.takenSibling.parent !== null,
          siblingPhase: director.siblingPhase,
          carried: director.carried,
          cries: director.cries,
          carriedAt: director.carriedAt,
        },
      };
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      if (siblingCollider !== null) world.colliders.remove(siblingCollider);
      siblingCollider = null;
      director.dispose();
    },
  };
}

export const BORN_PART_MODULE: BeatPartModuleDefinition = {
  beat: 1,
  part: 1,
  room: 'farrowing',
  create: createBornPartModule,
};
